import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Phone, Mail, FolderLock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { ClientAccessPanel } from './chat/ClientAccessPanel';
import { accessT } from './chat/chatAccessI18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  it: {
    title: 'Assistente Virtuale',
    subtitle: 'Studio Notarile Accordini & Carillo',
    placeholder: 'Scrivi la tua domanda...',
    welcome: 'Buongiorno! Sono l\'assistente virtuale dello Studio Notarile Accordini & Carillo. Come posso aiutarla oggi?',
    sending: 'Invio...',
    error: 'Si è verificato un errore. Riprova.',
    contact: 'Contattaci direttamente:',
    phone: 'Chiama',
    email: 'Email'
  },
  en: {
    title: 'Virtual Assistant',
    subtitle: 'Notary Office Accordini & Carillo',
    placeholder: 'Type your question...',
    welcome: 'Good day! I\'m the virtual assistant of Notary Office Accordini & Carillo. How can I help you today?',
    sending: 'Sending...',
    error: 'An error occurred. Please try again.',
    contact: 'Contact us directly:',
    phone: 'Call',
    email: 'Email'
  },
  de: {
    title: 'Virtueller Assistent',
    subtitle: 'Notariat Accordini & Carillo',
    placeholder: 'Schreiben Sie Ihre Frage...',
    welcome: 'Guten Tag! Ich bin der virtuelle Assistent des Notariats Accordini & Carillo. Wie kann ich Ihnen heute helfen?',
    sending: 'Senden...',
    error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    contact: 'Kontaktieren Sie uns direkt:',
    phone: 'Anrufen',
    email: 'E-Mail'
  },
  fr: {
    title: 'Assistant Virtuel',
    subtitle: 'Étude Notariale Accordini & Carillo',
    placeholder: 'Écrivez votre question...',
    welcome: 'Bonjour! Je suis l\'assistant virtuel de l\'Étude Notariale Accordini & Carillo. Comment puis-je vous aider aujourd\'hui?',
    sending: 'Envoi...',
    error: 'Une erreur s\'est produite. Veuillez réessayer.',
    contact: 'Contactez-nous directement:',
    phone: 'Appeler',
    email: 'Email'
  }
};

const generateSessionId = () => {
  return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const Chatbot = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.it;
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [accessOpen, setAccessOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, accessOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: t.welcome,
        timestamp: new Date()
      }]);
    }
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen, t.welcome]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage.content,
        session_id: sessionId,
        language: language
      });

      const reply = response.data.response;
      if (reply.includes('[[IDENTIFICA]]')) setAccessOpen(true);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: reply.replace('[[IDENTIFICA]]', '').trim(),
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t.error,
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        data-testid="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 w-14 h-14 bg-[#C9A227] hover:bg-[#B8911F] text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-36 md:bottom-24 right-3 md:right-6 z-50 w-[380px] max-w-[calc(100vw-24px)] max-h-[calc(100vh-170px)] md:max-h-none bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#283880] p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C9A227] rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{t.title}</h3>
                  <p className="text-slate-400 text-xs">{t.subtitle}</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="h-[350px] min-h-0 flex-1 md:flex-none overflow-y-auto p-4 bg-slate-50 space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 bg-[#283880] rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-[#C9A227]" />
                    </div>
                  )}
                  <div
                    data-testid={msg.testId}
                    className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-[#C9A227] text-white rounded-br-md'
                        : msg.isError
                        ? 'bg-red-100 text-red-700 rounded-bl-md'
                        : 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.role === 'user' ? msg.content : msg.content.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                      part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part.replace(/^#{1,4}\s/gm, '')
                    )}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-slate-600" />
                    </div>
                  )}
                </motion.div>
              ))}
              
              {accessOpen && (
                <ClientAccessPanel sessionId={sessionId} language={language} onClose={() => setAccessOpen(false)}
                  onSummary={(content) => setMessages(prev => [...prev, { role: 'assistant', content, timestamp: new Date(), testId: 'chat-new-docs-message' }])} />
              )}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-2 justify-start"
                >
                  <div className="w-8 h-8 bg-[#283880] rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-[#C9A227]" />
                  </div>
                  <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-slate-100">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Contact */}
            <div className="px-4 py-2 bg-slate-100 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-2">{t.contact}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="chatbot-my-documents"
                  onClick={() => setAccessOpen(true)}
                  className="flex-1 flex items-center justify-center gap-1 bg-white border border-[#283880] text-[#283880] text-xs py-2 px-2 rounded-lg hover:bg-[#283880] hover:text-white transition-colors"
                >
                  <FolderLock className="w-3 h-3" />
                  {(accessT[language] || accessT.it).myDocs}
                </button>
                <a
                  href="tel:+390456370799"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 flex items-center justify-center gap-1 bg-[#283880] text-white text-xs py-2 px-3 rounded-lg hover:bg-slate-800 transition-colors active:bg-slate-900"
                >
                  <Phone className="w-3 h-3" />
                  {t.phone}
                </a>
                <a
                  href="mailto:info@accordinicarillo.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 flex items-center justify-center gap-1 bg-[#C9A227] text-white text-xs py-2 px-3 rounded-lg hover:bg-[#B8911F] transition-colors active:bg-[#2A3F90]"
                >
                  <Mail className="w-3 h-3" />
                  {t.email}
                </a>
              </div>
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-slate-200">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  data-testid="chatbot-input"
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t.placeholder}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5FC1] focus:border-transparent disabled:bg-slate-50"
                />
                <button
                  data-testid="chatbot-send"
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="w-10 h-10 bg-[#C9A227] hover:bg-[#B8911F] disabled:bg-slate-200 text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
